/* global BigInt */

import { numericRetailDifference } from "./portableCheckContract";

// CHECK display/actions only. Source decimal text is never converted to Number.

export function formatEuropeanDecimal(token) {

    if (typeof token !== "string" || token.length > 1024 || !/^-?(0|[1-9]\d*)(\.\d+)?$/.test(token)) return "Unavailable";

    const [integer, fraction = ""] = token.split(".");

    return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${fraction.padEnd(2, "0")}`;

}

const text = value => value == null || value === "" ? "Unavailable" : Array.isArray(value) ? value.join(" · ") : String(value);

const price = (amount, currency) => {

    const formatted = formatEuropeanDecimal(amount);

    return formatted === "Unavailable" ? formatted : `${formatted}${/^[A-Z]{3}$/.test(currency || "") ? ` · ${currency}` : ""}`;

};

function decimal(token) {

    if (typeof token !== "string" || token.length > 1024 || !/^(0|[1-9]\d*)(\.\d+)?$/.test(token)) return null;

    const [integer, fraction = ""] = token.split(".");

    if (fraction.length > 500 || integer.length + fraction.length > 1000) return null;

    return { coefficient: BigInt(integer + fraction), scale: fraction.length };

}

function aligned(before, incoming) {

    const a = decimal(before), b = decimal(incoming);

    if (!a || !b) return null;

    const scale = Math.max(a.scale, b.scale);

    return { before: a.coefficient * BigInt(10) ** BigInt(scale - a.scale), incoming: b.coefficient * BigInt(10) ** BigInt(scale - b.scale), scale };

}

const retailField = product => (product.fields || product.changes || []).find(c => c.field === "retail");

export function filteredReportProducts(review) {

    return (review?.products || []).filter(numericRetailDifference);

}

function signed(value, scale) {

    const negative = value < BigInt(0), digits = (negative ? -value : value).toString().padStart(scale + 1, "0");

    const token = (negative ? "-" : "") + (scale ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}` : digits);

    return `${value > BigInt(0) ? "+" : ""}${formatEuropeanDecimal(token)}`;

}

export function retailDifference(before, incoming) {

    const v = aligned(before, incoming);

    if (!v) return "—";

    const delta = v.incoming - v.before;

    if (v.before === BigInt(0)) return `${signed(delta, v.scale)} (—)`;

    const numerator = delta * BigInt(10000), absolute = numerator < BigInt(0) ? -numerator : numerator;

    let hundredths = absolute / v.before;

    if ((absolute % v.before) * BigInt(2) >= v.before) hundredths += BigInt(1);

    if (numerator < BigInt(0)) hundredths = -hundredths;

    return `${signed(delta, v.scale)} (${signed(hundredths, 2)}%)`;

}

export function productReportRows(product) {

    const field = key => (product.fields || product.changes || []).find(c => c.field === key);

    const name = field("name"), retail = field("retail"), season = field("season"), purchase = product.purchase;

    return [

        { key: "name", label: "Product name", before: text(name?.before), incoming: text(name?.incoming), difference: "—",

            note: name?.verified ? "Values verified; export action is not implied" : "Comparison basis unverified" },

        { key: "retail", label: "Retail Price", before: price(retail?.before, product.retailDisplayCurrency), incoming: price(retail?.incoming, product.retailDisplayCurrency),

            beforeToken: retail?.before, incomingToken: retail?.incoming, difference: retailDifference(retail?.before, retail?.incoming),

            note: retail?.verified ? `Price list ${retail.priceListID}; ${retail.exportDisposition === "preserve-existing" ? "existing price preserved" : "no overwrite verified"}` : "Retail price unverified; no overwrite verified" },

        { key: "purchase", label: "Purchase Price", before: price(purchase?.before, purchase?.incomingCurrency), incoming: price(purchase?.incoming, purchase?.incomingCurrency),

            beforeToken: purchase?.before, incomingToken: purchase?.incoming, difference: retailDifference(purchase?.before, purchase?.incoming),

            note: "Provisional FS InPrice — purchase value/comparison unverified. Incoming wholesale is before FX/tariffs; no overwrite verified." },

        { key: "season", label: "Season", before: text(season?.before), incoming: text(season?.incoming), difference: "—",

            note: season?.verified ? "Values verified; export action is not implied" : "Comparison basis unverified" }

    ];

}

// Both actions consume this same frozen, complete snapshot, never visible DOM rows.

export const visibleReportReason = reason => typeof reason === "string" && !/CURRENCY|currency(?:[ -]verification|[ -](?:unverified|not verified))/i.test(reason);

export function posDisplayName(review) {

    const name = review?.posDisplay?.name;

    return typeof name === "string" && name.trim() && name.length <= 256 && !/[\u0000-\u001f\u007f]/.test(name) ? name : "POS";

}

export function coverageSummary(review, processing = false) {

    const checking = processing || review?.processing || review?.status === "checking";

    const c = review?.itemCoverage, count = value => Number.isSafeInteger(value) && value >= 0;

    const n = count(c?.preparedItems) ? c.preparedItems : "Counting…";

    const g = count(c?.orderedGTINs) ? c.orderedGTINs : count(review?.selectionSummary?.barcodes) ? review.selectionSummary.barcodes : "—";

    const u = count(c?.orderedUnits) ? c.orderedUnits : count(review?.selectionSummary?.units) ? review.selectionSummary.units : "—";

    const m = count(c?.matchedItems) && count(c?.preparedItems) && c.matchedItems <= c.preparedItems ? c.matchedItems : "—";

    return `Ordered: ${n} ${n === 1 ? "item" : "items"} · ${g} ${g === 1 ? "barcode" : "barcodes"} · ${u} ${u === 1 ? "unit" : "units"} | Matched in ${posDisplayName(review)}: ${checking ? "Checking…" : `${m} of ${n}`}`;

}

export function displayedDifferenceLabel(snapshot, processing = false) {

    if (processing || snapshot?.processing) return "Checking…";

    if (snapshot?.zeroUnconfirmed) return "Unconfirmed";
    return snapshot && Array.isArray(snapshot.products) ? `${snapshot.products.length} ${snapshot.products.length === 1 ? "item" : "items"}` : "Unavailable";

}

export function emptyReportMessage(review, processing = false) {

    if (processing || review?.processing || review?.status === "checking") return "Checking prices…";

    if (review?.terminalReason || review?.progress?.state === "stopped" || ["error", "incomplete", "unavailable", "stale"].includes(review?.status))

        return review?.message || "Stopped — verification incomplete";

    return review?.coverage?.existing === 0 ? `No matching ${posDisplayName(review)} products were returned.`

        : "No RRP differences found in the products compared.";

}

export function createReportSnapshot(review, processing = false) {

    const checking = processing || review?.processing || review?.status === "checking";

    if (review?.kind !== "order-differences" || !["partial", "complete"].includes(review.status) || !review.coverage || !Array.isArray(review.products)) return null;

    const selectedProducts = filteredReportProducts(review);
    const zeroUnconfirmed = !!review.manualReport && selectedProducts.length === 0 &&
        (!!review.manualReport.error || review.manualReport.available !== true);

    const summary = coverageSummary(review, checking);

    const lines = ["Front Systems CHECK — informational only", text(review.message), `Order: ${text(review.selectionSummary?.orderNumber ?? review.context?.orderID)}`,

        `Store: ${text(review.selectionSummary?.store ?? review.context?.storeID)} · Warehouse: ${text(review.selectionSummary?.warehouse ?? review.context?.warehouseID)}`,

        summary, `Items with differences: ${checking ? "Checking…" : zeroUnconfirmed ? "Unconfirmed" : `${selectedProducts.length} ${selectedProducts.length === 1 ? "item" : "items"}`}`,

        "Only products with comparable, numerically different RRP amounts are listed. Numeric differences do not establish price-list or overwrite authority.",

        "Purchase is provisional; unverified prices are not proposed overwrites. No export performed."];

    const header = Object.freeze([...lines]);

    const products = selectedProducts.map(product => {

        const rows = productReportRows(product).map(row => Object.freeze(row));

        lines.push("", `FS ProductID ${product.productID}`);

        for (const row of rows) lines.push(`${row.label}: FS ${row.before} | Incoming ${row.incoming} | Difference ${row.difference} | ${row.note}`);

        return Object.freeze({ productID: product.productID, rows: Object.freeze(rows) });

    });

    if (!products.length) lines.push(emptyReportMessage(review));

    lines.push("", ...(review.reasons || []).filter(visibleReportReason).map(reason => `Limitation: ${reason}`));

    return Object.freeze({ processing: !!checking, zeroUnconfirmed, summary, posName: posDisplayName(review), displayedDifferenceCount: products.length, emptyMessage: emptyReportMessage(review), labelBrandName: typeof review.selectionSummary?.brand === "string" ? review.selectionSummary.brand : null,

        pdfHeader: Object.freeze(header.filter(line => /^(Order:|Store:|Ordered:|Items with differences:)/.test(line))), subject: "Front Systems CHECK report", text: lines.join("\r\n"), header,

        limitations: Object.freeze((review.reasons || []).filter(visibleReportReason)), products: Object.freeze(products) });

}

export function emailDraft(snapshot) {

    // Existing mailto compose flow; no recipient or automatic send. Large reports use

    // a complete local .eml draft rather than silently truncating a mailto URL.

    const url = `mailto:?subject=${encodeURIComponent(snapshot.subject)}&body=${encodeURIComponent(snapshot.text)}`;

    return url.length <= 1800 ? { url } : { filename: "Front-Systems-CHECK.eml", content:

        `Subject: ${snapshot.subject}\r\nX-Unsent: 1\r\nMIME-Version: 1.0\r\nContent-Type: text/plain; charset=UTF-8\r\nContent-Transfer-Encoding: 8bit\r\n\r\n${snapshot.text}` };

}

export function printDocument(snapshot) {

    const escaped = snapshot.text.replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

    return `<!doctype html><html><head><meta charset="UTF-8"><title>Front Systems CHECK</title><style>pre{white-space:pre-wrap;overflow-wrap:anywhere;font:10pt/1.4 sans-serif}</style></head><body><pre>${escaped}</pre></body></html>`;

}



// Completion and evidence qualification are separate. Unknown matches never mean new/zero.

// Safe support codes remain on review.manualReport, never interpolated into UI copy.
export function manualReportNotice(report) {
    if (!report) return null;
    const prior = report.available ? " The previous saved report and count are retained." : " No saved count is verified.";
    if (report.error === "REPORT_READ_FAILED") return "The saved report could not be loaded. No CHECK started. Close and try opening it again.";
    if (report.error === "CHECK_RESULT_UNQUALIFIED") return "The result could not be verified and was not saved." + prior;
    if (report.state === "uncertain" || /UNCERTAIN|UNKNOWN|UNVERIFIED/.test(report.error || "")) return "The CHECK or save outcome is uncertain. No automatic retry was started." + prior;
    if (report.error === "REPORT_CONTEXT_CHANGED") return "The order or configuration changed. This result was not saved." + prior;
    if (report.error?.startsWith("REPORT_")) return "The CHECK result could not be saved." + prior;
    if (report.error) return "CHECK could not complete the calculation." + prior;
    if (report.state === "running") return "UPDATE is in progress. The previous saved report and count are retained.";
    if (report.available) return "Report and count saved.";
    return "No report/count saved yet.";
}

export function checkCompletion(review) {
    if (review?.manualReport?.error === "REPORT_READ_FAILED") return "Saved report unavailable";
    if (review?.manualReport?.error === "CHECK_RESULT_UNQUALIFIED") return "Result unconfirmed";
    if (review?.manualReport?.error || review?.manualReport?.state === "uncertain") return "CHECK/save incomplete";

    if (review?.terminalReason || review?.progress?.state === "stopped" || ["error", "stale", "unavailable", "incomplete"].includes(review?.status)) return "Stopped — verification incomplete";

    if (review?.progress?.state === "finished" || review?.status === "complete") return "Finished";

    return "Incomplete";

}

export function checkLimitations(review) {

    const failed = (review?.lookupCounts?.failed || review?.progress?.outcomes?.failed || 0) > 0;

    if (failed) return "Some product reads failed. Their results remain unknown; verification is incomplete.";

    if (review?.coverage?.existing === 0 && review?.coverage?.unknown > 0) return "No returned matches were verified. Unmatched items remain unverified, not confirmed absent.";

    if (review?.status === "partial") return "Processing has finished, but coverage or comparison fields remain unverified. See the coverage and limitations below.";

    return null;

}

